import { apiSlice } from "../../../features/api/apiSlice";

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}
function pickArr(res: any): any[] {
  const d = pick(res);
  return Array.isArray(d) ? d : [];
}

export const chiefExaminerApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Active executive set ────────────────────────────────────────────────
    chiefGetActiveSet: builder.query<{ id: number; name: string; start_date: string; end_date: string } | null, void>({
      query: () => "/exam/chief/active-set",
      transformResponse: (res: any) => pick(res),
      providesTags: [{ type: "ChiefExaminers" as const, id: "ACTIVE_SET" }],
    }),

    // ── Active exam diet ────────────────────────────────────────────────────
    chiefGetActiveDiet: builder.query<{ id: string; title: string; status: string } | null, void>({
      query: () => "/exam/chief/active-diet",
      transformResponse: (res: any) => pick(res),
      providesTags: [{ type: "ChiefExaminers" as const, id: "ACTIVE_DIET" }],
    }),

    // ── Exam board committee members (flat array — used by dashboard + assign pages)
    chiefGetExaminers: builder.query<any[], void>({
      query: () => "/exam/chief/examiners",
      transformResponse: (res: any) => pickArr(res),
      providesTags: [{ type: "ChiefExaminers" as const, id: "LIST" }],
    }),

    // ── Exam board committee metadata (id + name) ───────────────────────────
    chiefGetExamCommittee: builder.query<{ id: number; name: string } | null, void>({
      query: () => "/exam/chief/exam-committee",
      transformResponse: (res: any) => pick(res),
      providesTags: [{ type: "ChiefExaminers" as const, id: "COMMITTEE" }],
    }),

    chiefCreateExaminer: builder.mutation<any, { name: string; email: string; phone?: string }>({
      query: (body) => ({ url: "/exam/chief/examiners", method: "POST", body }),
      invalidatesTags: [{ type: "ChiefExaminers" as const, id: "LIST" }],
    }),

    chiefDeleteExaminer: builder.mutation<any, string>({
      query: (id) => ({ url: `/exam/chief/examiners/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "ChiefExaminers" as const, id: "LIST" }],
    }),

    // ── Course assignments per diet ─────────────────────────────────────────
    chiefGetAssignments: builder.query<any[], string | void>({
      query: (dietId) => `/exam/chief/assignments${dietId ? `?diet_id=${dietId}` : ""}`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_, __, dietId) => [
        { type: "ChiefAssignments" as const, id: dietId ?? "ALL" },
      ],
    }),

    chiefAssignCourse: builder.mutation<any, { examiner_id: string; course_id: string; diet_id: string }>({
      query: (body) => ({ url: "/exam/chief/assign-course", method: "POST", body }),
      invalidatesTags: (_, __, { diet_id }) => [
        { type: "ChiefAssignments" as const, id: diet_id },
      ],
    }),

    chiefRemoveAssignment: builder.mutation<any, string>({
      query: (id) => ({ url: `/exam/chief/assignments/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "ChiefAssignments" as const, id: "ALL" }],
    }),

    // ── Results review queue ────────────────────────────────────────────────
    chiefGetResults: builder.query<any[], { dietId?: string; status?: string } | void>({
      query: (params) => {
        const q = new URLSearchParams();
        if ((params as any)?.dietId) q.set("diet_id", (params as any).dietId);
        if ((params as any)?.status) q.set("status", (params as any).status);
        const s = q.toString();
        return `/exam/chief/results${s ? `?${s}` : ""}`;
      },
      transformResponse: (res: any) => pickArr(res),
      providesTags: [{ type: "ChiefResults" as const, id: "LIST" }],
    }),

    chiefRequestReview: builder.mutation<any, { id: string; comment: string }>({
      query: ({ id, comment }) => ({
        url: `/exam/chief/results/${id}/review`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: [{ type: "ChiefResults" as const, id: "LIST" }],
    }),

    chiefApproveResults: builder.mutation<any, string>({
      query: (id) => ({ url: `/exam/chief/results/${id}/approve`, method: "POST" }),
      invalidatesTags: [{ type: "ChiefResults" as const, id: "LIST" }],
    }),

    chiefGetBatchScores: builder.query<any[], string>({
      query: (id) => `/exam/chief/results/${id}/scores`,
      transformResponse: (res: any) => {
        const d = res?.data?.data ?? res?.data ?? res;
        return Array.isArray(d) ? d : [];
      },
      providesTags: (_, __, id) => [{ type: "ChiefResults" as const, id }],
    }),

    chiefForwardToExaminer: builder.mutation<any, { id: string; comment: string }>({
      query: ({ id, comment }) => ({
        url: `/exam/chief/results/${id}/forward-to-examiner`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: [{ type: "ChiefResults" as const, id: "LIST" }],
    }),

    // ── All exams (read-only list for chief) ───────────────────────────────
    chiefGetExams: builder.query<any[], void>({
      query: () => "/exam/chief/exams",
      transformResponse: (res: any) => pickArr(res),
      providesTags: [{ type: "ChiefExaminers" as const, id: "EXAMS" }],
    }),

    // ── Courses available to assign (linked to diets) ──────────────────────
    chiefGetCourses: builder.query<any[], string | void>({
      query: (dietId) => `/exam/chief/courses${dietId ? `?diet_id=${dietId}` : ""}`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_, __, dietId) => [
        { type: "ChiefCourses" as const, id: dietId ?? "ALL" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useChiefGetActiveSetQuery,
  useChiefGetActiveDietQuery,
  useChiefGetExaminersQuery,
  useChiefGetExamCommitteeQuery,
  useChiefCreateExaminerMutation,
  useChiefDeleteExaminerMutation,
  useChiefGetAssignmentsQuery,
  useChiefAssignCourseMutation,
  useChiefRemoveAssignmentMutation,
  useChiefGetResultsQuery,
  useChiefRequestReviewMutation,
  useChiefApproveResultsMutation,
  useChiefGetBatchScoresQuery,
  useChiefForwardToExaminerMutation,
  useChiefGetCoursesQuery,
  useChiefGetExamsQuery,
} = chiefExaminerApi;
